import { Link, useNavigate } from '@tanstack/react-router';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Stage = { kind: 'request' } | { kind: 'confirm'; email: string } | { kind: 'done' };

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'request' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleRequest(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    setBusy(true);
    setError(null);
    try {
      await resetPassword({ username: email });
      setStage({ kind: 'confirm', email });
    } catch (err) {
      setError(messageFromError(err) ?? 'Could not start password reset.');
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (stage.kind !== 'confirm') return;
    const form = new FormData(e.currentTarget);
    const code = String(form.get('code') ?? '').trim();
    const newPassword = String(form.get('newPassword') ?? '');
    setBusy(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: stage.email,
        confirmationCode: code,
        newPassword,
      });
      setStage({ kind: 'done' });
    } catch (err) {
      setError(messageFromError(err) ?? 'Could not reset password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/20 px-6 py-12">
      <div className="w-full max-w-sm space-y-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
        >
          <ArrowLeft className="size-3" />
          Back to sign in
        </Link>

        {stage.kind === 'request' && (
          <form onSubmit={handleRequest} className="space-y-6">
            <header className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">Reset password</h1>
              <p className="text-sm text-muted-foreground">
                Enter your email and we'll send you a verification code.
              </p>
            </header>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                disabled={busy}
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy && <Loader2 className="animate-spin" />}
              Send code
            </Button>
          </form>
        )}

        {stage.kind === 'confirm' && (
          <form onSubmit={handleConfirm} className="space-y-6">
            <header className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">Enter new password</h1>
              <p className="text-sm text-muted-foreground">
                Code sent to <span className="font-medium text-foreground">{stage.email}</span>.
              </p>
            </header>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <Input id="code" name="code" inputMode="numeric" required disabled={busy} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New password</Label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  disabled={busy}
                />
                <p className="text-xs text-muted-foreground">
                  12+ characters, upper + lower case, number, symbol.
                </p>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy && <Loader2 className="animate-spin" />}
              Reset password
            </Button>
          </form>
        )}

        {stage.kind === 'done' && (
          <div className="space-y-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Password reset</h1>
            <p className="text-sm text-muted-foreground">
              You can now sign in with your new password.
            </p>
            <Button type="button" className="w-full" onClick={() => navigate({ to: '/login' })}>
              Go to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function messageFromError(err: unknown): string | null {
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
    return err.message;
  }
  return null;
}
